import type {
  FindingEditableField,
  PendingAiEdit,
  PendingAiFieldEdit,
} from '../store/findingEditStore';

export const FINDING_FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  shortDescription: 'Short Description',
  description: 'Description',
  poc: 'Proof of Concept',
  pocBlocks: 'PoC Blocks',
  businessImpact: 'Business Impact',
  mitigationPlan: 'Mitigation Plan',
  status: 'Status',
  severity: 'Severity',
  category: 'Category',
  assignedTo: 'Assignee',
  findingUpdateComment: 'Update Comment',
  cvss: 'CVSS',
  cvss40: 'CVSS 4.0',
  cvss31: 'CVSS 3.1',
  severityReason: 'Severity reason',
  owaspRisk: 'OWASP Risk',
};

export function getFindingFieldLabel(field: FindingEditableField): string {
  return FINDING_FIELD_LABELS[field] ?? field;
}

export function getPendingFieldEdit({
  pendingAiEdits,
  field,
}: {
  pendingAiEdits?: PendingAiEdit;
  field: FindingEditableField;
}): PendingAiFieldEdit | undefined {
  return pendingAiEdits?.fields.find((entry) => entry.field === field);
}

const SCROLL_TOP_CLEARANCE_PX = 16;
const COPILOT_EDIT_FIELDS_SCROLL_SELECTOR = '.copilot-progressing-fields';

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function resolveFieldChromeElement(element: HTMLElement): HTMLElement {
  const scopedFieldRoot = element.closest('[data-scope="field"]');

  if (scopedFieldRoot instanceof HTMLElement) {
    return scopedFieldRoot;
  }

  const parent = element.parentElement;

  if (
    parent instanceof HTMLElement &&
    parent !== document.body &&
    parent.querySelector('label')
  ) {
    return parent;
  }

  return element;
}

function resolvePendingFieldElement(
  field: FindingEditableField,
): HTMLElement | null {
  const reviewCard = document.querySelector(
    `[data-pending-review-field="${field}"]`,
  );

  if (reviewCard instanceof HTMLElement) {
    return resolveFieldChromeElement(reviewCard);
  }

  const anchor = document.querySelector(`[data-field-anchor="${field}"]`);

  return anchor instanceof HTMLElement
    ? resolveFieldChromeElement(anchor)
    : null;
}

export function scrollToPendingField(
  field: FindingEditableField,
  options?: { behavior?: ScrollBehavior },
): void {
  const element = resolvePendingFieldElement(field);

  if (!element) {
    return;
  }

  const behavior =
    options?.behavior ?? (prefersReducedMotion() ? 'auto' : 'smooth');
  const scrollContainer = element.closest(COPILOT_EDIT_FIELDS_SCROLL_SELECTOR);

  if (!(scrollContainer instanceof HTMLElement)) {
    element.style.scrollMarginTop = `${SCROLL_TOP_CLEARANCE_PX}px`;
    element.scrollIntoView({ behavior, block: 'start' });

    return;
  }

  const containerRect = scrollContainer.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const nextScrollTop =
    elementRect.top -
    containerRect.top +
    scrollContainer.scrollTop -
    SCROLL_TOP_CLEARANCE_PX;
  const maxScrollTop = Math.max(
    0,
    scrollContainer.scrollHeight - scrollContainer.clientHeight,
  );

  scrollContainer.scrollTo({
    top: Math.min(Math.max(0, nextScrollTop), maxScrollTop),
    behavior,
  });
}

export function scrollToPendingFieldAfterLayout(
  field: FindingEditableField,
): () => void {
  let cancelled = false;
  let outerFrameId = 0;
  let innerFrameId = 0;
  let timeoutId: number | undefined;

  outerFrameId = window.requestAnimationFrame(() => {
    innerFrameId = window.requestAnimationFrame(() => {
      timeoutId = window.setTimeout(() => {
        if (!cancelled) {
          scrollToPendingField(field, { behavior: 'auto' });
        }
      }, 0);
    });
  });

  return () => {
    cancelled = true;
    window.cancelAnimationFrame(outerFrameId);
    window.cancelAnimationFrame(innerFrameId);

    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
    }
  };
}

export function getNextPendingReviewField({
  pendingAiEdits,
  currentField,
  pendingBeforeResolve,
}: {
  pendingAiEdits?: PendingAiEdit;
  currentField: FindingEditableField;
  pendingBeforeResolve?: PendingAiEdit;
}): FindingEditableField | undefined {
  const remaining = pendingAiEdits?.fields ?? [];

  if (remaining.length === 0) {
    return undefined;
  }

  const orderedSource = pendingBeforeResolve?.fields ?? remaining;
  const currentIndex = orderedSource.findIndex(
    (entry) => entry.field === currentField,
  );

  if (currentIndex < 0) {
    return remaining[0]?.field;
  }

  const isStillPending = (field: FindingEditableField): boolean =>
    remaining.some((entry) => entry.field === field) && field !== currentField;

  for (let index = currentIndex + 1; index < orderedSource.length; index += 1) {
    const field = orderedSource[index]?.field;

    if (field && isStillPending(field)) {
      return field;
    }
  }

  for (let index = currentIndex - 1; index >= 0; index -= 1) {
    const field = orderedSource[index]?.field;

    if (field && isStillPending(field)) {
      return field;
    }
  }

  return undefined;
}

export function mergeReviewSessionFields({
  sessionFields,
  pendingFields,
  isNewReview,
}: {
  sessionFields: readonly FindingEditableField[];
  pendingFields: readonly FindingEditableField[];
  isNewReview: boolean;
}): FindingEditableField[] {
  if (pendingFields.length === 0) {
    return [...sessionFields];
  }

  if (isNewReview || sessionFields.length === 0) {
    return [...pendingFields];
  }

  const next = [...sessionFields];

  for (const field of pendingFields) {
    if (!next.includes(field)) {
      next.push(field);
    }
  }

  return next;
}

export type ReviewPillState =
  | { kind: 'hidden' }
  | {
      kind: 'reviewing';
      field: FindingEditableField;
      current: number;
      total: number;
    };

export function getReviewPillState({
  sessionFields,
  pendingCount,
  focusedField,
}: {
  sessionFields: readonly FindingEditableField[];
  pendingCount: number;
  focusedField?: FindingEditableField;
}): ReviewPillState {
  if (sessionFields.length === 0 || pendingCount === 0) {
    return { kind: 'hidden' };
  }

  const total = sessionFields.length;
  const focusedIndex = focusedField ? sessionFields.indexOf(focusedField) : -1;
  const index = focusedIndex >= 0 ? focusedIndex : 0;
  const field = sessionFields[index];

  if (!field) {
    return { kind: 'hidden' };
  }

  return {
    kind: 'reviewing',
    field,
    current: index + 1,
    total,
  };
}
